import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { CriticZone } from 'src/app/core/interfaces/critic-zone';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { PlanningRoutesMessages } from 'src/app/core/messages/planning-routes-messages.enum';
import { Global } from 'src/app/core/resources/global';
import { Utils } from 'src/app/core/resources/utils';
import { GoogleService } from 'src/app/core/services/google.service';
import { PlanningRouteService } from 'src/app/core/services/planning-route.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { StandardMapComponent } from 'src/app/shared/standard-map/standard-map.component';
declare var google: any;

@Component({
  selector: 'app-critic-zones-form',
  templateUrl: './critic-zones-form.component.html',
  styleUrls: ['./critic-zones-form.component.scss']
})
export class CriticZonesFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(StandardMapComponent) standardMap: StandardMapComponent;
  form: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(5)]),
    reason: new FormControl('', [Validators.required, Validators.minLength(8)]),
    startAlias: new FormControl(''),
    endAlias: new FormControl(''),
    polylines: new FormControl('', [Validators.required])
  });
  startAddressControl = new FormControl('', [Validators.required]);
  endAddressControl = new FormControl('', [Validators.required]);
  startAddressInitialValue = '';
  endAddressInitialValue = '';
  startAddressData: { address: string, location: google.maps.LatLngLiteral };
  endAddressData: { address: string, location: google.maps.LatLngLiteral };
  mapClickTarget: 'start' | 'end' = 'start';
  validate = '';
  startSub: Subscription;
  endSub: Subscription;
  mapOptions: OptionsAutocomplete = {
    initialClickMap: true,
    clickMapIcon: this.global.pathMarkerCriticZone
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      zone?: CriticZone,
      mode?: 'create' | 'edit' | 'view'
    },
    public dialogRef: MatDialogRef<CriticZonesFormComponent>,
    private planningRouteService: PlanningRouteService,
    private snackbarService: SnackBarService,
    public utils: Utils,
    public spinner: NgxSpinnerService,
    public global: Global,
    private googleService: GoogleService
  ) {
    if (this.data && this.data.zone)
      this.patchZone(this.data.zone);
    this.mapOptions.initialClickMap = !this.isViewMode;
  }

  private patchZone(zone: CriticZone) {
    this.form.get('name').setValue(zone.name || '');
    this.form.get('reason').setValue(zone.reason || '');
    this.form.get('startAlias').setValue(zone.start && zone.start.alias ? zone.start.alias : '');
    this.form.get('endAlias').setValue(zone.end && zone.end.alias ? zone.end.alias : '');
    this.form.get('polylines').setValue(zone.polylines || '');
    if (zone.start && zone.start.location) {
      this.startAddressData = {
        address: zone.start.name,
        location: zone.start.location
      };
      this.startAddressInitialValue = zone.start.name;
      this.startAddressControl.setValue(this.startAddressData);
    }
    if (zone.end && zone.end.location) {
      this.endAddressData = {
        address: zone.end.name,
        location: zone.end.location
      };
      this.endAddressInitialValue = zone.end.name;
      this.endAddressControl.setValue(this.endAddressData);
    }
  }

  ngOnInit() {
    this.setSubscriptions();
    if (this.isViewMode) {
      this.form.disable();
      this.startAddressControl.disable();
      this.endAddressControl.disable();
      this.validate = 'disable';
    }
  }

  ngAfterViewInit() {
    if (this.hasCompleteZone) setTimeout(() => this.refreshMapPreview(), 0);
  }

  onMapReady() {
    if (this.hasCompleteZone) this.refreshMapPreview();
  }

  get mode(): 'create' | 'edit' | 'view' {
    if (this.data && this.data.mode) return this.data.mode;
    return this.data && this.data.zone ? 'edit' : 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get title(): string {
    if (this.mode === 'view') return 'Detalle de zona crítica';
    if (this.mode === 'edit') return 'Actualizar zona crítica';
    return 'Crear zona crítica';
  }

  get actionLabel(): string {
    return this.mode === 'edit' ? 'Actualizar zona crítica' : 'Crear zona crítica';
  }

  get hasCompleteZone(): boolean {
    return !!(this.startAddressData && this.startAddressData.location
      && this.endAddressData && this.endAddressData.location
      && this.form.get('polylines').value);
  }

  setSubscriptions() {
    if (this.isViewMode) return;
    this.startSub = this.startAddressControl.valueChanges.subscribe((value) => {
      if (value && value.address && value.location) {
        this.startAddressData = value;
        this.updatePolyline();
      } else {
        this.startAddressData = null;
        this.form.get('polylines').setValue('');
        this.clearMapPreview();
      }
    });
    this.endSub = this.endAddressControl.valueChanges.subscribe((value) => {
      if (value && value.address && value.location) {
        this.endAddressData = value;
        this.updatePolyline();
      } else {
        this.endAddressData = null;
        this.form.get('polylines').setValue('');
        this.clearMapPreview();
      }
    });
  }

  async onClickMap($event: any) {
    if (this.isViewMode) return;
    const lat = $event.latLng.lat();
    const lng = $event.latLng.lng();
    const address = await this.transformCoordinatesToAddress(lat, lng);
    const location = { lat, lng };
    if (!address || !location)
      return this.snackbarService.openSnackBar('No se pudo obtener una dirección válida para el punto seleccionado.', undefined, 'alert');
    const value = { address, location };
    if (this.mapClickTarget === 'start') {
      this.startAddressInitialValue = address;
      this.startAddressControl.setValue(value);
    } else {
      this.endAddressInitialValue = address;
      this.endAddressControl.setValue(value);
    }
  }

  async transformCoordinatesToAddress(lat: number, lng: number): Promise<string> {
    const geocoderService = new google.maps.Geocoder();
    const latlng = { lat, lng };
    let address = '';
    this.spinner.show();
    await new Promise<void>(resolve => {
      geocoderService.geocode({ location: latlng }, (results: any[], status: any) => {
        this.spinner.hide();
        if (status === 'OK' && results.length) {
          address = results[0].formatted_address;
        } else {
          this.snackbarService.openSnackBar('No se pudo obtener la dirección para la ubicación seleccionada.', undefined, 'error');
        }
        resolve();
      });
    });
    return address;
  }

  async updatePolyline() {
    if (this.isViewMode) return;
    if (!this.startAddressData || !this.startAddressData.location || !this.endAddressData || !this.endAddressData.location) return;
    if (this.startAddressData.location.lat === this.endAddressData.location.lat
      && this.startAddressData.location.lng === this.endAddressData.location.lng) {
      this.form.get('polylines').setValue('');
      this.snackbarService.openSnackBar('El origen y el destino de la zona crítica no pueden ser iguales.', undefined, 'alert');
      this.clearMapPreview();
      return;
    }
    try {
      this.spinner.show();
      const data = await this.googleService.getRouteData(
        [this.startAddressData.location, this.endAddressData.location],
        'WALKING',
        false
      ) as { cargoDistancy: number, cargoEstimatedTime: number, cargoRoute: google.maps.DirectionsResult };
      this.spinner.hide();
      const route = data && data.cargoRoute && data.cargoRoute.routes && data.cargoRoute.routes.length
        ? data.cargoRoute.routes[0]
        : null;
      const encoded = this.planningRouteService.getEncodedPolylineFromRoute(route);
      if (!encoded) {
        this.snackbarService.openSnackBar('No fue posible calcular la polilínea de la zona crítica.', undefined, 'error');
        this.form.get('polylines').setValue('');
        this.clearMapPreview();
        return;
      }
      this.form.get('polylines').setValue(encoded);
      this.refreshMapPreview();
    } catch (error) {
      console.error(error);
      this.spinner.hide();
      this.form.get('polylines').setValue('');
      this.clearMapPreview();
      this.snackbarService.openSnackBar('No se pudo encontrar una ruta entre el origen y el destino.', undefined, 'error');
    }
  }

  private refreshMapPreview() {
    if (!this.standardMap) return;
    const preview = this.buildPayload();
    if (!preview) return this.clearMapPreview();
    this.standardMap.renderCriticZones([preview], 'critic-zone-form');
    this.standardMap.fitMapToCriticZone(preview);
    this.standardMap.removeMarker(this.mapOptions.keyMarkerListener ? this.mapOptions.keyMarkerListener : 'clickMapMarker');
  }

  private clearMapPreview() {
    if (this.standardMap) this.standardMap.clearCriticZones('critic-zone-form');
  }

  private buildPayload(): CriticZone {
    if (!this.startAddressData || !this.startAddressData.location || !this.endAddressData || !this.endAddressData.location) return null;
    const startAlias = (this.form.get('startAlias').value || '').trim();
    const endAlias = (this.form.get('endAlias').value || '').trim();
    const payload: CriticZone = {
      start: {
        location: this.startAddressData.location,
        name: this.startAddressData.address
      },
      end: {
        location: this.endAddressData.location,
        name: this.endAddressData.address
      },
      name: (this.form.get('name').value || '').trim(),
      reason: (this.form.get('reason').value || '').trim(),
      polylines: this.form.get('polylines').value
    };
    if (startAlias) {
      payload.start.alias = startAlias;
    }
    if (endAlias) {
      payload.end.alias = endAlias;
    }
    return payload;
  }

  create() {
    this.form.markAllAsTouched();
    this.validate = 'touched';
    if (this.startAddressControl.invalid) return this.snackbarService.openSnackBar("Debe seleccionar un origen válido.", undefined, 'alert');
    if (this.endAddressControl.invalid) return this.snackbarService.openSnackBar("Debe seleccionar un destino válido.", undefined, 'alert');
    if (this.startAddressData.address === this.endAddressData.address)
      return this.snackbarService.openSnackBar("El origen y el destino no pueden ser iguales.", undefined, 'alert');
    if (this.form.invalid) {
      if (this.utils.errorMessagesCustomized(this.form.get('name'), 'nombre', 5)) return;
      if (this.utils.errorMessagesCustomized(this.form.get('reason'), 'motivo', 8)) return;
      return this.snackbarService.openSnackBar(PlanningRoutesMessages.INCOMPLETE_FORM, undefined, 'error');
    }
    const payload = this.buildPayload();
    if (!payload || !payload.polylines)
      return this.snackbarService.openSnackBar("Debe seleccionar un origen y un destino válidos.", undefined, 'alert');

    this.spinner.show();
    if (this.mode === 'edit' && this.data.zone && this.data.zone.id) {
      payload.id = this.data.zone.id;
      this.planningRouteService.updateCriticZone(payload).subscribe(
        () => {
          this.spinner.hide();
          this.snackbarService.openSnackBar(PlanningRoutesMessages.UPDATED_CRITIC_ZONE, undefined, 'success');
          this.dialogRef.close({ state: true });
        },
        () => {
          this.spinner.hide();
          this.snackbarService.openSnackBar(PlanningRoutesMessages.HTTP_ERROR, undefined, 'error');
        }
      );
      return;
    }

    this.planningRouteService.createCriticZone(payload).subscribe(
      () => {
        this.spinner.hide();
        this.snackbarService.openSnackBar(PlanningRoutesMessages.CREATED_CRITIC_ZONE);
        this.dialogRef.close({ state: true });
      },
      () => {
        this.spinner.hide();
        this.snackbarService.openSnackBar(PlanningRoutesMessages.HTTP_ERROR, undefined, 'error');
      }
    );
  }

  ngOnDestroy() {
    if (this.startSub) {
      this.startSub.unsubscribe();
    }
    if (this.endSub) {
      this.endSub.unsubscribe();
    }
  }
}
